/**
 * MobileStepIndicator
 *
 * Compact top-bar progress indicator shown on mobile/tablet (< lg) where
 * the full StepRail sidebar is hidden. Renders:
 *
 *   - Brand mark on the left
 *   - "Step X of Y" label in the middle
 *   - A thin progress bar beneath
 *
 * Pure presentational — all state derived from props.
 */

import { Sparkles } from 'lucide-react';

export interface MobileStepIndicatorProps {
  current: number;
  total: number;
  /** Optional short label for the current step — shown on the right */
  currentLabel?: string;
}

export function MobileStepIndicator({ current, total, currentLabel }: MobileStepIndicatorProps) {
  const progress = Math.min(100, Math.max(0, (current / total) * 100));

  return (
    <div className="border-b border-white/5 bg-background/80 backdrop-blur">
      <div className="flex items-center gap-3 px-5 pt-4 pb-3">
        {/* Brand */}
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-linear-to-br from-primary to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.35)]">
          <Sparkles className="h-4 w-4 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">
            Onboarding Process
          </span>
          <span className="truncate text-sm font-semibold text-white">
            {currentLabel ?? `Step ${current} of ${total}`}
          </span>
        </div>
        <span className="shrink-0 text-xs font-medium text-muted tabular-nums">
          {current}/{total}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden bg-secondary/60">
        <div
          className="h-full bg-linear-to-r from-primary to-blue-400 transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
